import { useState, useRef } from 'react';

import { useParams, useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import { BsPencil, BsTrash } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import IconButton from '#globalComponents/IconButton/IconButton';
import { ConfirmModal } from '#globalComponents/Overlay/ConfirmModal';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';
import { useErrorStore } from '#lib/stores/errorStore';

import CustomFieldsSection from '../../components/common/CustomFieldsSection/CustomFieldsSection';
import ProductHeader from '../../components/product/ProductHeader/ProductHeader';
import { useVisibleFieldDefs } from '../../models/customFields/queries';
import { useUpdateProduct, useDeleteProduct } from '../../models/products/mutations';
import { useProductsList, useBundlesList, useProductDeals } from '../../models/products/queries';
import { buildDefaultFieldPatch } from '../../utils/fieldValues';
import { formatPrice } from '../../utils/formatters';

// Linked Deals (read-only sidebar)

function LinkedDealsPanel({ productId }) {
	const { data: dealsData } = useProductDeals(productId);
	const deals = dealsData?.deals ?? [];
	return (
		<div>
			<h3 className="text-xs font-semibold text-bgBase-800 uppercase tracking-wide mb-2">
				In Deals · {deals.length}
			</h3>
			{deals.length === 0 ? (
				<p className="text-xs text-bgBase-800 py-2">Keinem Deal zugeordnet.</p>
			) : (
				<div className="space-y-1.5">
					{deals.map((d) => (
						<div key={d.id} className="flex items-center justify-between py-1.5 text-xs">
							<div className="min-w-0">
								<div className="font-medium text-text truncate">{d.title}</div>
								<div className="text-bgBase-800">{d.companyNameSnapshot}</div>
							</div>
							<span className="flex-shrink-0 font-bold tabular-nums text-text ml-3">
								{formatPrice(d.dealValue)}
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

LinkedDealsPanel.propTypes = { productId: PropTypes.string.isRequired };

// Linked Bundles (read-only sidebar)

function LinkedBundlesPanel({ productId, bundles }) {
	const used = (bundles ?? []).filter((b) => b.items?.some((it) => it.productId === productId));
	return (
		<div>
			<h3 className="text-xs font-semibold text-bgBase-800 uppercase tracking-wide mb-2">
				In Paketen · {used.length}
			</h3>
			{used.length === 0 ? (
				<p className="text-xs text-bgBase-800 py-2">Nicht in Paketen enthalten.</p>
			) : (
				<div className="space-y-1.5">
					{used.map((b) => (
						<div key={b.id} className="flex items-center justify-between py-1.5 text-xs">
							<span className="text-text font-medium">{b.name}</span>
							<span className="text-bgBase-800 font-semibold">
								×{b.items.find((it) => it.productId === productId)?.quantity ?? 1}
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

LinkedBundlesPanel.propTypes = { productId: PropTypes.string.isRequired, bundles: PropTypes.array };

// Product Info Card (Click-to-Edit, like CompanyInfoCard)

function ProductInfoCard({ product, fieldDefs, onSave, saving, editTriggerRef }) {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState({});

	const startEdit = () => {
		const d = { _customFields: { ...(product.customFields ?? {}) } };
		for (const def of fieldDefs) {
			if (def.isDefault) d[def.fieldKey] = product[def.fieldKey] ?? '';
		}
		setDraft(d);
		setEditing(true);
	};

	// Expose startEdit to parent via ref
	if (editTriggerRef) editTriggerRef.current = startEdit;

	const handleSave = async () => {
		const payload = buildDefaultFieldPatch(fieldDefs, draft, product);
		const origCf = product.customFields ?? {};
		const draftCf = draft._customFields ?? {};
		if (JSON.stringify(origCf) !== JSON.stringify(draftCf)) {
			payload.customFields = draftCf;
		}
		if (Object.keys(payload).length > 0) await onSave(payload);
		setEditing(false);
	};

	return (
		<Card className="p-5 space-y-4">
			{editing ? (
				<CustomFieldsSection
					entityType="product"
					entity={{ ...product, ...draft }}
					values={draft._customFields ?? product.customFields ?? {}}
					inline
					showLabel={false}
					onChangeEntity={(fieldKey, value) => setDraft((d) => ({ ...d, [fieldKey]: value }))}
					onChange={(fieldId, value) =>
						setDraft((d) => ({ ...d, _customFields: { ...(d._customFields ?? {}), [fieldId]: value } }))
					}
				/>
			) : (
				<CustomFieldsSection
					entityType="product"
					entity={product}
					values={product.customFields ?? {}}
					onSave={(cf) => onSave({ customFields: cf })}
					onSaveEntity={(fieldKey, value) => onSave({ [fieldKey]: value })}
					readOnly
					showLabel={false}
				/>
			)}

			{editing && (
				<div className="flex justify-end gap-2 pt-2 border-t border-bgBase-400">
					<Button onClick={() => setEditing(false)} variant="outline" size="sm">
						Abbrechen
					</Button>
					<Button onClick={handleSave} size="sm" loading={saving}>
						Speichern
					</Button>
				</div>
			)}
		</Card>
	);
}

ProductInfoCard.propTypes = {
	product: PropTypes.object.isRequired,
	fieldDefs: PropTypes.array.isRequired,
	onSave: PropTypes.func.isRequired,
	saving: PropTypes.bool,
	editTriggerRef: PropTypes.object,
};

// Page Component

export default function CrmProductDetailPage() {
	const { productId } = useParams();
	const navigate = useNavigate();
	const [deleteOpen, setDeleteOpen] = useState(false);
	const editTriggerRef = useRef(null);

	const showError = useErrorStore((s) => s.showError);
	const showSuccess = useErrorStore((s) => s.showSuccess);

	const { data: productsData, isLoading: productsLoading } = useProductsList();
	const { data: bundlesData } = useBundlesList();
	const { data: productFieldDefs = [] } = useVisibleFieldDefs('product');

	const products = productsData?.products ?? [];
	const bundles = bundlesData?.bundles ?? [];
	const product = products.find((p) => p.id === productId);

	const updateMut = useUpdateProduct();
	const deleteMut = useDeleteProduct();

	const handleSave = async (payload) => {
		try {
			await updateMut.mutateAsync({ id: productId, payload });
			showSuccess({ title: 'Produkt gespeichert' });
		} catch (err) {
			showError({ error: err, title: 'Fehler beim Speichern' });
			throw err;
		}
	};

	const handleDelete = async () => {
		try {
			await deleteMut.mutateAsync(productId);
			showSuccess({ title: 'Produkt gelöscht' });
			navigate('/crm/products');
		} catch (err) {
			showError({ error: err, title: 'Fehler beim Löschen' });
		}
	};

	if (productsLoading)
		return (
			<Page>
				<div className="text-center text-bgBase-800 py-20">Laden...</div>
			</Page>
		);
	if (!product) {
		navigate('/crm/products', { replace: true });
		return null;
	}

	return (
		<Page
			header={
				<PageHeader
					breadcrumbs={[
						{ label: 'CRM', to: '/crm' },
						{ label: 'Produkte', to: '/crm/products' },
						product.name,
					]}
				>
					<div className="flex items-center gap-2">
						<Button onClick={() => editTriggerRef.current?.()} variant="outline" size="sm">
							<BsPencil className="w-3 h-3" /> Bearbeiten
						</Button>
						<IconButton icon={BsTrash} label="Löschen" tone="danger" onClick={() => setDeleteOpen(true)} />
					</div>
				</PageHeader>
			}
		>
			<ProductHeader product={product} />

			<div className="pb-8">
				<div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
					<ProductInfoCard
						product={product}
						fieldDefs={productFieldDefs}
						onSave={handleSave}
						saving={updateMut.isPending}
						editTriggerRef={editTriggerRef}
					/>

					<div className="space-y-6">
						<Card>
							<LinkedDealsPanel productId={product.id} />
						</Card>
						<Card>
							<LinkedBundlesPanel productId={product.id} bundles={bundles} />
						</Card>
					</div>
				</div>
			</div>

			<ConfirmModal
				isOpen={deleteOpen}
				onClose={() => setDeleteOpen(false)}
				onConfirm={handleDelete}
				title="Produkt löschen"
				message={`"${product.name}" wirklich löschen?`}
				isLoading={deleteMut.isPending}
			/>
		</Page>
	);
}
